import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Instagram, Linkedin, Github, Mail } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CONTACT_EMAIL } from "@/lib/brand";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { footerCopy } from "@/lib/siteCopy";

const PRODUCT_HREFS = ["/#products", "/#products", "/#products", "/#products"];
const SERVICE_HREFS = [
  "/services/web-app#web-development",
  "/services/devops-cloud",
  "/services/web-app#android-app-development",
  "/services/web-app#apis-integrations",
  "/services/ai-automation",
  "/services/support-maintenance",
];
const NAV_HREFS = ["/", "/services", "/work", "/about", "/careers", "/contact"];

export function Footer() {
  const lang = useSiteLanguage();
  const t = footerCopy[lang];

  return (
    <footer className="w-full rounded-t-[3rem] bg-black px-6 py-12 text-white">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <div className="mb-4">
              <BrandLogo size="lg" variant="dark" textClassName="text-white" />
            </div>
            <p className="max-w-[280px] text-sm leading-relaxed text-zinc-500">
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                {t.tagline}
              </span>
            </p>

            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>

            <div className="mt-6 flex items-center space-x-4">
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="group rounded-lg p-2 transition-all duration-300 hover:bg-zinc-900 hover:shadow-lg hover:shadow-pink-500/20"
              >
                <Instagram className="h-5 w-5 text-zinc-500 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(236,72,153,0.6)]" />
              </a>
              <a
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="group rounded-lg p-2 transition-all duration-300 hover:bg-zinc-900 hover:shadow-lg hover:shadow-blue-500/20"
              >
                <Linkedin className="h-5 w-5 text-zinc-500 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
              </a>
              <a
                href="https://x.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="group rounded-lg p-2 transition-all duration-300 hover:bg-zinc-900 hover:shadow-lg hover:shadow-sky-400/20"
              >
                <svg
                  className="h-5 w-5 text-zinc-500 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(56,189,248,0.6)]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://github.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="group rounded-lg p-2 transition-all duration-300 hover:bg-zinc-900 hover:shadow-lg hover:shadow-gray-400/20"
              >
                <Github className="h-5 w-5 text-zinc-500 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(156,163,175,0.6)]" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              {t.navigation}
            </h3>
            <ul className="space-y-3 text-sm text-zinc-500">
              {t.navLinks.map((label, i) => (
                <li key={NAV_HREFS[i]}>
                  <Link
                    to={NAV_HREFS[i]!}
                    className="transition-colors hover:text-white"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              {t.products}
            </h3>
            <ul className="space-y-3 text-sm text-zinc-500">
              {t.productLinks.map((label, i) => (
                <li key={label}>
                  <Link
                    to={PRODUCT_HREFS[i]!}
                    className="transition-colors hover:text-white"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              {t.services}
            </h3>
            <ul className="space-y-3 text-sm text-zinc-500">
              {t.serviceLinks.map((label, i) => (
                <li key={SERVICE_HREFS[i]}>
                  <Link
                    to={SERVICE_HREFS[i]!}
                    className="transition-colors hover:text-white"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="bg-zinc-800 opacity-20" />

        <div className="flex flex-col items-center justify-between gap-4 py-6 sm:flex-row">
          <p className="text-[11px] tracking-wide text-zinc-600">{t.rights}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-zinc-500">
            <Link to="/privacy" className="transition-colors hover:text-white">
              {t.privacy}
            </Link>
            <Link to="/terms" className="transition-colors hover:text-white">
              {t.terms}
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="transition-colors hover:text-white"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <div className="relative flex flex-col items-center justify-center pb-10 pt-10">
          <div className="relative inline-block">
            <h1 className="select-none text-[14vw] font-bold leading-none tracking-tighter text-zinc-800 md:text-[180px]">
              DevDotCom
            </h1>
            <h1
              className="animate-sweep-glow absolute inset-0 select-none bg-clip-text text-[14vw] font-bold leading-none tracking-tighter text-transparent md:text-[180px]"
              style={{
                backgroundImage:
                  "linear-gradient(-30deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.2) 40%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0.2) 60%, rgba(255,255,255,0) 70%)",
                backgroundSize: "300% 100%",
              }}
            >
              DevDotCom
            </h1>
          </div>
        </div>
      </div>
    </footer>
  );
}
